import React from "react";
import { useFormik, FormikProvider } from "formik";
import { Dialog } from "primereact/dialog";
import { IUser } from "@/constants/interfaces";
import { UserValidationSchema } from "@/constants/validationSchema";
import Input from "@/components/forms/Input";
import Select from "@/components/forms/Select";
import { Button } from "primereact/button";
import { toast } from "react-toastify";
import { InputSwitch } from "primereact/inputswitch";

interface Props {
  show: boolean;
  onHide: () => void;
  user: any;
  setShowEdit: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function EditUser({ show, onHide, user, setShowEdit }: Props) {
  const isUpdate = user.id ? true : false;
  const [loading, setLoading] = React.useState(false);

  const data = {
    firstname: user?.name?.split(" ")[0],
    lastname: user?.name?.split(" ")[1],
    email: user.email,
    phone: user.phone,
    role: user.role,
    status: user.status,
    assignedTo: user.assignedTo,
  };

  const formik = useFormik({
    initialValues: {
      firstname: data.firstname ?? "",
      lastname: data.lastname ?? "",
      email: data.email ?? "",
      phone: data.phone ?? "",
      role: data.role ?? "",
      status: data.status ?? "",
      assignedTo: data.assignedTo ?? [],
    },
    validationSchema: UserValidationSchema,
    enableReinitialize: true,
    onSubmit: (values: IUser) => {
      // simulate api call
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
        setShowEdit(false);
        toast.success(`User ${isUpdate ? "Updated" : "Added"} Successfully`);
      }, 500);
    },
  });
  const { handleSubmit, values } = formik;

  return (
    <Dialog
      header={isUpdate ? "Edit User" : "Add User"}
      visible={show}
      onHide={onHide}
      style={{ width: "50vw" }}
      breakpoints={{ "960px": "75vw", "641px": "100vw" }}
      blockScroll={true}
    >
      <FormikProvider value={formik}>
        <div className="flex flex-column gap-4">
          <section className="flex flex-row gap-4 grid">
            <Input
              id="firstname"
              label="First Name"
              placeholder="eg. John"
              isRequired
            />
            <Input
              id="lastname"
              label="Last Name"
              placeholder="eg. John"
              isRequired
            />
          </section>

          <Input
            id="email"
            icon="pi pi-envelope"
            label="Email"
            placeholder="eg. name@company.com"
            isRequired
          />

          <Input
            id="phone"
            icon="pi pi-phone"
            label="Phone Number"
            placeholder="eg. 0200000000"
            isRequired
            type="tel"
            pattern="[0-9]{10}"
            maxLength={10}
          />

          <section className="flex flex-row gap-5 align-items-center">
            <Select
              id="role"
              placeholder="Select Role"
              label="Role"
              isRequired
              options={[
                { label: "Super Admin", value: "Super Admin" },
                { label: "Inputer", value: "Inputer" },
              ]}
              optionLabel="label"
            />

            <div className="col flex flex-column gap-2">
              <label htmlFor="status" className="p-d-block">
                Status
              </label>

              <div className="flex flex-row gap-4 align-items-center">
                <p>{values.status === "Active" ? "Active" : "Inactive"}</p>
                <InputSwitch
                  id="status"
                  checked={values.status === "Active" ? true : false}
                  defaultChecked={values.status === "Active" ? true : false}
                  onChange={(e) => {
                    formik.setFieldValue(
                      "status",
                      e.value ? "Active" : "Inactive"
                    );
                  }}
                />
              </div>
            </div>
          </section>

          <Button
            label={isUpdate ? "Update User" : "Add User"}
            className="text-white mt-5"
            loading={loading}
            onClick={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          />
        </div>
      </FormikProvider>
    </Dialog>
  );
}
